import React, { useEffect, useRef, useState } from 'react';
import { Camera, Loader2, Plus, Trash2 } from 'lucide-react';
import {
  caricaFotoVerbale, eliminaFotoVerbale, ErroreSocial, FotoVerbale, leggiFotoVerbale,
} from '../../services/verbaliService';
import { useToast } from '../../contexts/ToastContext';
import ConfirmDialog from '../ConfirmDialog';

/**
 * Foto dell'evento per il post social (piano 3). Non vanno nel verbale: servono solo al post.
 * Anteprime lette con il Bearer come `blob:` (revocati quando la foto sparisce e allo smontaggio);
 * caricamenti uno alla volta fino ai posti liberi. Con `caricabile` falso (post non disponibile)
 * le foto già caricate si possono solo vedere ed eliminare.
 */

export const MAX_FOTO = 5;
const MAX_BYTES = 15 * 1024 * 1024;
const ACCETTATE = 'image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif';
export const TESTO_MASSIMO_FOTO = 'Puoi caricare al massimo 5 foto';

/** Messaggi chiari per i codici d'errore delle foto (altrimenti quello del server). */
const MESSAGGI_FOTO: Record<string, string> = {
  FOTO_NON_LEGGIBILE: 'Non riesco a leggere questa foto: prova con un\'altra o esportala in JPEG',
  FOTO_MANCANTE: 'La foto non è arrivata al server: sceglila di nuovo',
  CARICAMENTO_NON_VALIDO: 'Caricamento non valido: scegli di nuovo la foto',
  TROPPE_FOTO: 'Troppi caricamenti in poco tempo: riprova tra qualche minuto',
  FOTO_TROPPO_GRANDE: 'La foto è troppo grande: massimo 15 MB',
  MAX_FOTO: TESTO_MASSIMO_FOTO,
};
/** Dopo questi codici gli altri file della scelta non si caricano. */
const FERMANO_LA_CODA = new Set(['TROPPE_FOTO', 'MAX_FOTO', 'SOCIAL_NON_DISPONIBILE']);

export const messaggioErroreFoto = (err: ErroreSocial | any): string =>
  (err?.codice && MESSAGGI_FOTO[err.codice]) || err?.messaggio || 'Caricamento della foto non riuscito';

interface VerbaleFotoProps {
  eventId: string;
  foto: FotoVerbale[];
  onCambia: (foto: FotoVerbale[]) => void;
  /** Post social disponibile: si possono aggiungere foto. Falso ⇒ solo vedere ed eliminare. */
  caricabile: boolean;
}

const VerbaleFoto: React.FC<VerbaleFotoProps> = ({ eventId, foto, onCambia, caricabile }) => {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const fotoRef = useRef(foto);
  fotoRef.current = foto;
  const [anteprime, setAnteprime] = useState<Record<string, string>>({});
  const urlRef = useRef<Map<string, string>>(new Map());
  const inLetturaRef = useRef<Set<string>>(new Set());
  const vivoRef = useRef(true);
  const [carico, setCarico] = useState<{ nome: string; percentuale: number } | null>(null);
  const [daEliminare, setDaEliminare] = useState<FotoVerbale | null>(null);

  useEffect(() => {
    vivoRef.current = true;
    const mappa = urlRef.current;
    return () => {
      vivoRef.current = false;
      mappa.forEach((url) => URL.revokeObjectURL(url));
      mappa.clear();
    };
  }, []);

  // Anteprime: lette per le foto nuove, revocate per quelle tolte.
  const chiave = foto.map((f) => f._id).join(',');
  useEffect(() => {
    const ids = new Set(fotoRef.current.map((f) => f._id));
    const mappa = urlRef.current;
    let tolte = false;
    mappa.forEach((url, fotoId) => {
      if (ids.has(fotoId)) return;
      URL.revokeObjectURL(url);
      mappa.delete(fotoId);
      tolte = true;
    });
    if (tolte) setAnteprime(Object.fromEntries(mappa));
    ids.forEach((fotoId) => {
      if (mappa.has(fotoId) || inLetturaRef.current.has(fotoId)) return;
      inLetturaRef.current.add(fotoId);
      leggiFotoVerbale(eventId, fotoId, 'anteprima')
        .then((blob) => {
          if (!vivoRef.current || !fotoRef.current.some((f) => f._id === fotoId)) return;
          const url = URL.createObjectURL(blob);
          mappa.set(fotoId, url);
          setAnteprime(Object.fromEntries(mappa));
        })
        .catch(() => { /* anteprima non disponibile: resta la casella con l'icona */ })
        .finally(() => { inLetturaRef.current.delete(fotoId); });
    });
  }, [chiave, eventId]);

  const liberi = Math.max(0, MAX_FOTO - foto.length);

  const scegli = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const scelti = Array.from(e.target.files || []);
    e.target.value = '';
    if (!scelti.length) return;
    const posti = Math.max(0, MAX_FOTO - fotoRef.current.length);
    if (scelti.length > posti) toast.warning(TESTO_MASSIMO_FOTO);
    let elenco = [...fotoRef.current];
    for (const file of scelti.slice(0, posti)) {
      if (file.size > MAX_BYTES) {
        toast.error(`«${file.name}» non caricata`, MESSAGGI_FOTO.FOTO_TROPPO_GRANDE);
        continue;
      }
      setCarico({ nome: file.name, percentuale: 0 });
      try {
        const nuova = await caricaFotoVerbale(eventId, file, (p) => {
          if (vivoRef.current) setCarico({ nome: file.name, percentuale: p });
        });
        if (!vivoRef.current) return;
        elenco = [...elenco, nuova];
        onCambia(elenco);
      } catch (err: any) {
        if (!vivoRef.current) return;
        toast.error(`«${file.name}» non caricata`, messaggioErroreFoto(err));
        if (FERMANO_LA_CODA.has(err?.codice)) break;
      }
    }
    if (vivoRef.current) setCarico(null);
  };

  const apriOriginale = async (f: FotoVerbale) => {
    // La scheda si apre dentro il clic, altrimenti il browser la blocca.
    const finestra = window.open('', '_blank');
    try {
      const blob = await leggiFotoVerbale(eventId, f._id, 'originale');
      const url = URL.createObjectURL(blob);
      if (finestra) finestra.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err: any) {
      finestra?.close();
      toast.error('Foto non disponibile', err?.messaggio);
    }
  };

  const elimina = async (f: FotoVerbale) => {
    try {
      await eliminaFotoVerbale(eventId, f._id);
      onCambia(fotoRef.current.filter((x) => x._id !== f._id));
      toast.success('Foto eliminata');
    } catch (err: any) {
      toast.error('Foto non eliminata', err?.messaggio);
    }
  };

  const caselleVuote = caricabile ? Array.from({ length: liberi }, (_, i) => i) : [];

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800" aria-label="Foto dell'evento">
      <div className="mb-2 flex items-center gap-2">
        <Camera className="h-4 w-4 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
        <span className="text-sm font-semibold text-slate-900 dark:text-white">Foto dell'evento</span>
      </div>
      <p className="mb-3 text-sm text-slate-500 dark:text-gray-400">
        {foto.length} di {MAX_FOTO} · non vanno nel verbale
      </p>

      {caricabile && (
        <input
          ref={inputRef}
          type="file"
          accept={ACCETTATE}
          multiple
          onChange={scegli}
          className="hidden"
          data-testid="verbale-foto-input"
        />
      )}

      <ul className="grid grid-cols-5 gap-2" aria-label="Foto caricate">
        {foto.map((f, i) => (
          <li key={f._id} className="group relative aspect-square overflow-hidden rounded-lg border border-slate-200 bg-slate-100 dark:border-gray-700 dark:bg-gray-900/50">
            <button
              type="button"
              onClick={() => apriOriginale(f)}
              aria-label={`Apri la foto ${i + 1}`}
              className="block h-full w-full"
            >
              {anteprime[f._id] ? (
                <img src={anteprime[f._id]} alt={`Foto ${i + 1}`} className="h-full w-full object-cover" />
              ) : (
                <span className="grid h-full w-full place-items-center text-slate-400">
                  <Camera className="h-4 w-4" aria-hidden="true" />
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => setDaEliminare(f)}
              aria-label={`Elimina la foto ${i + 1}`}
              className="absolute right-0.5 top-0.5 grid h-6 w-6 place-items-center rounded-md bg-white/90 text-slate-500 shadow-sm hover:bg-red-50 hover:text-red-600 dark:bg-gray-800/90 dark:text-gray-300 dark:hover:bg-red-900/40 dark:hover:text-red-300"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </li>
        ))}
        {caselleVuote.map((i) => (
          <li key={`vuota-${i}`} className="aspect-square">
            {i === 0 && carico ? (
              <span
                role="status"
                className="grid h-full w-full place-items-center rounded-lg border border-dashed border-emerald-300 bg-emerald-50 text-xs font-medium text-emerald-700 dark:border-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300"
                aria-label={`Caricamento di ${carico.nome}`}
              >
                <span className="flex flex-col items-center gap-0.5">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  {carico.percentuale}%
                </span>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={!!carico}
                aria-label="Aggiungi foto"
                className="grid h-full w-full place-items-center rounded-lg border border-dashed border-slate-300 text-slate-400 hover:border-emerald-400 hover:bg-emerald-50 hover:text-emerald-600 disabled:opacity-50 dark:border-gray-600 dark:hover:border-emerald-600 dark:hover:bg-emerald-900/20 dark:hover:text-emerald-300"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </li>
        ))}
      </ul>

      {!caricabile && foto.length > 0 && (
        <p className="mt-2 text-xs text-slate-500 dark:text-gray-400">
          Il post social non è disponibile: puoi vedere ed eliminare le foto già caricate.
        </p>
      )}
      <p className="mt-2 text-xs text-slate-500 dark:text-gray-400">
        Le foto restano 30 giorni dopo l'invio del post, poi si cancellano
      </p>

      <ConfirmDialog
        isOpen={!!daEliminare}
        onClose={() => setDaEliminare(null)}
        onConfirm={() => { if (daEliminare) elimina(daEliminare); }}
        title="Eliminare la foto?"
        message="La foto non sarà più tra quelle del post social."
        confirmText="Elimina"
        type="danger"
      />
    </section>
  );
};

export default VerbaleFoto;
